import { Box, Card, Grid, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AppFieldView from '@/common/components/form-view/AppFieldView';

import { useNavigate, useParams } from 'react-router-dom';

import {
	useDeleteFamilyMemberListMutation,
	useGetFamilyDetailQuery,
	useQueryFamilyGroupListQuery,
} from '@/api/family-management/familyApi';
import { showLabel } from '@/utils/helper';
import { YESNO } from '@/common/constants/common';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';
import { GridColDef } from '@mui/x-data-grid';
import AddMemberToListDialog from './AddMemberToListDialog';
import GroupRemoveOutlinedIcon from '@mui/icons-material/GroupRemoveOutlined';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import AddFamilyMemberToListDialog from './AddFamilyMemberToListDialog';
import { useGetUser } from '@/hooks/useGetUser';
import { PermissionEnum } from '@/api/enum/common.enum';

interface Props {
	details: any;
}
export default function FamilyPage(props: Props) {
	const { details } = props;
	const { t } = useTranslation();
	const { admissionRequestId } = useParams();
	const showModal = useModal();
	const [deleteFamilyMemberList] = useDeleteFamilyMemberListMutation();
	const { userInfo } = useGetUser();

	const { data: groupDetails } = useQueryFamilyGroupListQuery(
		admissionRequestId || '',
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const columns: GridColDef[] = [
		{
			field: 'targetMemberNameEn',
			headerName: t('member.name_en'),
			flex: 15,
		},
		{
			field: 'targetMemberNameZh',
			headerName: t('member.name_zh'),
			flex: 15,
		},
		{
			field: 'relationshipDesc',
			headerName: t('preAdmission.relationship'),
			flex: 25,
		},

		{
			field: 'targetMemberPhone',
			headerName: t('family.mobilePhone'),
			flex: 15,
		},
		{
			field: 'targetMemberFileNo',
			headerName: t('family.memberFileNo'),
			flex: 15,
			renderCell: (params) => (
				<a
					href={`/member-management/member-list/detail/${params.row.targetMemberIdDesc}`}
					target='_blank'
					rel='noopener noreferrer'
				>
					{params.row.targetMemberFileNo}
				</a>
			),
		},
		{
			field: 'targetFamilyMemberFileNo',
			headerName: t('family.familyMemberFileNo'),
			flex: 15,
			renderCell: (params) => (
				<a
					href={`/family-management/family-management-list/detail/${params.row.targetFamilyMemberIdDesc}`}
					target='_blank'
					rel='noopener noreferrer'
				>
					{params.row.targetFamilyMemberFileNo}
				</a>
			),
		},
		{
			field: 'targetFamilyMembership',
			headerName: t('family.membershipRole'),
			flex: 15,
		},
		{
			field: 'membershipStateDesc',
			headerName: t('member.isCase'),
			flex: 15,
			renderCell: (params) => showLabel(params.row.isCase, YESNO),
		},
		{
			field: 'delete',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<IconButton
					color='error'
					onClick={() => {
						handleDelete(params.row.targetMemberId);
					}}
				>
					<GroupRemoveOutlinedIcon />
				</IconButton>
			),
		},
	];

	const handleDelete = async (id: string) => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={false}
				title={t('family.removeConfirmation')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('family.removeConfirmationText')}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.confirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteFamilyMemberList(id || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.deleteMemberListSuccess'), {
							variant: 'warning',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});
					}
				}}
			/>
		));
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Card
					sx={{
						padding: ' 24px 24px 0',
						marginTop: 3,
					}}
				>
					<Grid container spacing={3} marginTop={0} marginBottom={3}>
						<Grid size={6}>
							<AppFieldView
								label={t('member.name_en')}
								value={details?.data.nameEn}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('member.name_zh')}
								value={details?.data.nameZh}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('family.memberFileNo')}
								value={details?.data.fileNo}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('family.familyMemberFileNo')}
								value={details?.data.familyMemberFileNo}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('family.membershipRole')}
								value={details?.data.membershipRole}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('member.isCase')}
								value={showLabel(details?.data.isCase, YESNO)}
							/>
						</Grid>
					</Grid>
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'flex-end',
							marginBottom: 2,
						}}
					>
						{userInfo?.permissionList?.includes(
							PermissionEnum.MemberManageFamilyMemberAddMember
						) && (
							<AddMemberToListDialog
								details={details}
								groupDetails={groupDetails}
							/>
						)}
						{userInfo?.permissionList?.includes(
							PermissionEnum.MemberManageFamilyMemberAddFamilyMember
						) && (
							<AddFamilyMemberToListDialog
								details={details}
								groupDetails={groupDetails}
							/>
						)}
					</Box>

					<Grid
						container
						spacing={2}
						marginTop={0}
						marginBottom={2}
						alignItems='center'
					>
						<Grid size={12} display='flex'>
							<LocalDataGrid
								rows={groupDetails?.data || []}
								columns={columns}
								getRowId={(row) => row.id}
								pagination={false}
							/>
						</Grid>
					</Grid>
				</Card>
			</Box>
		</>
	);
}
